import React, { Children, useMemo, isValidElement, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import classnames from 'classnames';
import { u as useDataListLayoutContext } from './DataListLayoutContext-CRuQjlBl.js';
import { I as InternalDataListItemActions } from './DataListItemActions-PJA3ny4I.js';
import { D as DataListActionsMenu } from './DataListActionsMenu-Cwed3dNg.js';
import { D as DataListLayoutActionsContext } from './DataListLayoutContext-B-xNSmCX.js';
import { d as generateListItemElement } from './DataList.utils-CKHBAG6U.js';
import { C as Checkbox } from './Checkbox-ugVz15Dc.js';
import { u as useBatchSelect } from './useBatchSelect-CALxOuDl.js';
import { s as styles } from './DataList-DeTUdKxc.js';
import { D as DataListItemClickable } from './DataListItemClickable-f2rFgaHL.js';
import { u as useDataListContext } from './DataListContext-CyU4jeBU.js';

function DataListItemInternal({ children, item, }) {
    const { canSelect, hasAtLeastOneSelected, isSelectAll, selectedIDs, selected, onSelect, } = useBatchSelect();
    if (!canSelect)
        return children;
    return (React.createElement("div", { className: classnames(styles.selectable, {
            [styles.selected]: hasAtLeastOneSelected,
        }) },
        children,
        React.createElement(Checkbox, { checked: getIsChecked(), onChange: handleChange })));
    function getIsChecked() {
        const isItemInSelectedIDs = selectedIDs.includes(item.id);
        // If we're in a "select all" state, the selectedID's becomes a list of
        // unchecked ID's.
        if (isSelectAll)
            return !isItemInSelectedIDs;
        // Otherwise, we're in a "select some" state, so we can just check if the
        // item is in the selectedIDs list.
        return isItemInSelectedIDs;
    }
    function handleChange() {
        if (isSelectAll)
            return handleSelectAllChange();
        return handleSelectSomeChange();
    }
    function handleSelectAllChange() {
        if (!selected || Array.isArray(selected))
            return;
        if (selectedIDs === null || selectedIDs === void 0 ? void 0 : selectedIDs.includes(item.id)) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(Object.assign(Object.assign({}, selected), { unselected: selectedIDs === null || selectedIDs === void 0 ? void 0 : selectedIDs.filter(id => id !== item.id) }));
        }
        else if (selectedIDs) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(Object.assign(Object.assign({}, selected), { unselected: [...selectedIDs, item.id] }));
        }
    }
    function handleSelectSomeChange() {
        if (selectedIDs === null || selectedIDs === void 0 ? void 0 : selectedIDs.includes(item.id)) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(selectedIDs === null || selectedIDs === void 0 ? void 0 : selectedIDs.filter(id => id !== item.id));
        }
        else if (selectedIDs) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect([...selectedIDs, item.id]);
        }
    }
}

function useGetItemActions(item) {
    const { itemActionComponent } = useDataListContext();
    const itemActions = (itemActionComponent === null || itemActionComponent === void 0 ? void 0 : itemActionComponent.props.children) || [];
    const actionsArray = Children.toArray(itemActions);
    const actions = useMemo(() => {
        return actionsArray.filter(action => {
            if (isValidElement(action) && action.props.visible) {
                return action.props.visible(item);
            }
            return true;
        });
    }, [itemActionComponent === null || itemActionComponent === void 0 ? void 0 : itemActionComponent.props.children, item]);
    return {
        actions: actions,
        hasActions: Boolean(actions.length),
    };
}

// eslint-disable-next-line max-statements
function DataListItem({ item, layout, }) {
    const { hasInLayoutActions } = useDataListLayoutContext();
    const [showMenu, setShowMenu] = useState(false);
    const [contextPosition, setContextPosition] = useState();
    const generatedItem = useMemo(() => generateListItemElement(item), [item]);
    const { actions, hasActions } = useGetItemActions(item);
    const isContextMenuVisible = Boolean(contextPosition);
    const shouldShowContextMenu = showMenu && isContextMenuVisible && hasActions;
    return (React.createElement(DataListLayoutActionsContext.Provider, { value: { activeItem: item } },
        React.createElement("div", { onMouseEnter: handleShowMenu, onMouseLeave: handleHideMenu, onFocus: handleShowMenu, onBlur: handleHideMenu, onContextMenu: handleContextMenu, className: classnames(styles.listItem, {
                [styles.active]: showMenu && isContextMenuVisible,
            }), key: item.id },
            React.createElement(DataListItemInternal, { item: item },
                React.createElement(DataListItemClickable, null, layout(generatedItem))),
            React.createElement(AnimatePresence, null,
                showMenu && hasActions && !hasInLayoutActions && (React.createElement(InternalDataListItemActions, { actions: actions })),
                React.createElement(DataListActionsMenu, { key: item.id, visible: shouldShowContextMenu, position: contextPosition || { x: 0, y: 0 }, onRequestClose: () => setContextPosition(undefined) }, actions)))));
    function handleShowMenu() {
        setShowMenu(true);
    }
    function handleHideMenu() {
        setShowMenu(false);
    }
    function handleContextMenu(event) {
        if (!hasActions || isContextMenuVisible)
            return;
        event.preventDefault();
        setContextPosition({
            x: event.clientX,
            y: event.clientY,
        });
    }
}

export { DataListItem as D };
